import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  getBinders,
  createBinder,
  updateBinder,
  deleteBinder,
} from "@/api/binders";
import { invalidateBinderWatchlistVariantsQueries } from "@/queries/common";

export interface Binder {
  id: number;
  name: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  total_sum: string;
  card_count: string;
  total_count?: string | number;
  unpriced_count?: string | number;
}

export interface BinderCard {
  id: number;
  name?: string | null;
  number: string;
  rarity: string;
  set_id: string;
  details: string;
  game_id: string;
  created_at: string;
  updated_at: string;
  external_id: string;
  // Optional variant-related properties
  variantId?: number;
  variantCondition?: string;
}

export const useBinders = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["binders", user?.id],
    queryFn: async () => {
      if (!user) return [];

      const response = await getBinders();

      if (!response.success) {
        throw new Error("Failed to fetch binders");
      }

      return response.data as Binder[];
    },
    enabled: !!user,
    retry: false,
  });
};

export const useCreateBinder = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ name }: { name: string }) => {
      if (!user) throw new Error("User not authenticated");

      const response = await createBinder(name);

      if (!response.success) {
        throw new Error("Failed to create binder");
      }

      return response.data;
    },
    onSuccess: () => {
      invalidateBinderWatchlistVariantsQueries();
      toast.success("Binder created successfully!");
    },
    onError: (error) => {
      console.error("Error creating binder:", error);
      toast.error("Failed to create binder");
    },
  });
};

export const useUpdateBinder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, name }: { id: number; name: string }) => {
      const response = await updateBinder(id, name);

      if (!response.success) {
        throw new Error("Failed to update binder");
      }

      return response.data;
    },
    onSuccess: () => {
      invalidateBinderWatchlistVariantsQueries();
      toast.success("Binder updated successfully!");
    },
    onError: (error) => {
      console.error("Error updating binder:", error);
      toast.error("Failed to update binder");
    },
  });
};

export const useDeleteBinder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (binderId: number) => {
      const response = await deleteBinder(binderId);

      if (!response.success) {
        throw new Error("Failed to delete binder");
      }
    },
    onSuccess: () => {
      invalidateBinderWatchlistVariantsQueries();
      toast.success("Binder deleted successfully!");
    },
    onError: (error) => {
      console.error("Error deleting binder:", error);
      toast.error("Failed to delete binder");
    },
  });
};
